import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TodayIcon from '@mui/icons-material/Today';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import HomeIcon from '@mui/icons-material/Home';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import LightModeIcon from '@mui/icons-material/LightMode';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SettingsIcon from '@mui/icons-material/Settings';
import { useGrow, GROW_STAGES } from '../context/GrowContext';

const STAGE_ORDER = ['seedling', 'vegetative', 'flower', 'lateFlower'];

// Environment options
const ENVIRONMENT_OPTIONS = [
  { value: 'indoor', label: 'Indoor', icon: <HomeIcon sx={{ fontSize: 16 }} /> },
  { value: 'outdoor', label: 'Outdoor', icon: <WbSunnyIcon sx={{ fontSize: 16 }} /> },
  { value: 'greenhouse', label: 'Greenhouse', icon: <HomeIcon sx={{ fontSize: 16 }} /> },
];

// Medium options
const MEDIUM_OPTIONS = [
  { value: 'hydroponic', label: 'Hydroponic' },
  { value: 'dwc', label: 'Deep Water Culture' },
  { value: 'soil', label: 'Soil' },
  { value: 'coco', label: 'Coco Coir' },
  { value: 'aeroponics', label: 'Aeroponics' },
  { value: 'nft', label: 'NFT' },
];

// Lighting options
const LIGHTING_OPTIONS = [
  { value: 'LED', label: 'LED' },
  { value: 'HPS', label: 'HPS' },
  { value: 'CMH', label: 'CMH' },
  { value: 'Fluorescent', label: 'Fluorescent' },
  { value: 'Natural', label: 'Natural Light' },
  { value: 'Mixed', label: 'Mixed' },
];

// Plant type options
const PLANT_TYPE_OPTIONS = [
  { value: 'cucumber', label: 'Cucumber' },
  { value: 'tomato', label: 'Tomato' },
  { value: 'pepper', label: 'Pepper' },
  { value: 'lettuce', label: 'Lettuce' },
  { value: 'herbs', label: 'Herbs' },
  { value: 'strawberry', label: 'Strawberry' },
  { value: 'microgreens', label: 'Microgreens' },
  { value: 'other', label: 'Other' },
];

// Helper to get today's date in YYYY-MM-DD format
const getTodayString = () => new Date().toISOString().split('T')[0];

function StartGrowDialog({ open, onClose, onStart, presets }) {
  const [growName, setGrowName] = React.useState('');
  const [selectedPreset, setSelectedPreset] = React.useState(presets[0]?.id || '');
  const [startDate, setStartDate] = React.useState(getTodayString());
  const [startStage, setStartStage] = React.useState('seedling');

  React.useEffect(() => {
    if (open) {
      setGrowName('');
      setStartDate(getTodayString());
      setStartStage('seedling');
      if (presets.length > 0) {
        setSelectedPreset(presets[0].id);
      }
    }
  }, [open, presets]);

  const handleStart = () => {
    if (selectedPreset) {
      const name = growName.trim() || `Grow ${new Date().toLocaleDateString()}`;
      onStart(name, selectedPreset, new Date(startDate + 'T00:00:00'), startStage);
      onClose();
    }
  };

  const handleSetToday = () => {
    setStartDate(getTodayString());
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
        Start New Grow
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Grow Name"
            value={growName}
            onChange={(e) => setGrowName(e.target.value)}
            placeholder="e.g., Spring Batch 2025"
            size="small"
            fullWidth
            autoFocus
          />

          <FormControl fullWidth size="small">
            <InputLabel>Select Preset</InputLabel>
            <Select
              value={selectedPreset}
              onChange={(e) => setSelectedPreset(e.target.value)}
              label="Select Preset"
            >
              {presets.map((preset) => (
                <MenuItem key={preset.id} value={preset.id}>
                  {preset.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <Tooltip title="Set to today">
                <IconButton
                  onClick={handleSetToday}
                  size="small"
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    mb: 0.5,
                  }}
                >
                  <TodayIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
              Starting Stage
            </Typography>
            <ToggleButtonGroup
              value={startStage}
              exclusive
              onChange={(_, v) => v && setStartStage(v)}
              size="small"
              fullWidth
            >
              {STAGE_ORDER.map((stage) => (
                <ToggleButton
                  key={stage}
                  value={stage}
                  sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.5 }}
                >
                  {GROW_STAGES[stage].order}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
              {GROW_STAGES[startStage].label}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleStart} variant="contained" startIcon={<PlayArrowIcon />}>
          Start Grow
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// End Grow Dialog component
function EndGrowDialog({ open, onClose, onEndGrow, growName }) {
  const handleAction = (action) => {
    if (action !== 'cancel') {
      onEndGrow(action);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              bgcolor: 'warning.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningAmberIcon sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '1rem' }}>
              End Grow
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {growName}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={1.5}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => handleAction('cancelled')}
            sx={{
              justifyContent: 'flex-start',
              px: 2,
              py: 1.5,
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'warning.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: 'warning.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5 }}>
              <CancelIcon sx={{ color: 'white', fontSize: 18 }} />
            </Box>
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>End Early</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif' }}>
                Mark as cancelled, keep in logs
              </Typography>
            </Box>
          </Button>

          <Button
            variant="outlined"
            fullWidth
            onClick={() => handleAction('completed')}
            sx={{
              justifyContent: 'flex-start',
              px: 2,
              py: 1.5,
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'success.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5 }}>
              <CheckCircleIcon sx={{ color: 'white', fontSize: 18 }} />
            </Box>
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Mark Complete</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif' }}>
                Successfully completed, keep in logs
              </Typography>
            </Box>
          </Button>

          <Button
            variant="outlined"
            fullWidth
            onClick={() => handleAction('delete')}
            sx={{
              justifyContent: 'flex-start',
              px: 2,
              py: 1.5,
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'error.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: 'error.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5 }}>
              <DeleteIcon sx={{ color: 'white', fontSize: 18 }} />
            </Box>
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Delete</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif' }}>
                Permanently remove from all logs
              </Typography>
            </Box>
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            fontFamily: 'Inter, sans-serif',
            textTransform: 'none',
            px: 3,
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Trait chip component
function TraitChip({ icon, label, value }) {
  return (
    <Chip
      icon={icon}
      label={`${label}: ${value}`}
      size="small"
      variant="outlined"
      sx={{
        fontFamily: 'Inter, sans-serif',
        fontSize: '0.7rem',
        height: 24,
        '& .MuiChip-icon': { fontSize: 14 },
      }}
    />
  );
}

export default function GrowSelector() {
  const {
    presets,
    grows,
    activeGrow,
    activeGrowId,
    setActiveGrowId,
    addGrow,
    updateGrow,
    endGrow,
    updateGrowStage,
  } = useGrow();

  const [editedName, setEditedName] = React.useState('');
  const [showDetails, setShowDetails] = React.useState(false);
  const [tagInput, setTagInput] = React.useState('');
  const [showEndGrowDialog, setShowEndGrowDialog] = React.useState(false);
  const [showStartDialog, setShowStartDialog] = React.useState(false);

  // Initialize edited name when entering edit mode
  React.useEffect(() => {
    if (showDetails && activeGrow) {
      setEditedName(activeGrow.name);
    }
  }, [showDetails, activeGrow]);

  const traits = activeGrow?.traits || {};

  const handleStageChange = (_, newStage) => {
    if (newStage && activeGrowId) {
      updateGrowStage(activeGrowId, newStage);
    }
  };

  const handleStartGrow = (name, presetId, startDate, startStage) => {
    addGrow(name, presetId, startDate, startStage);
  };

  const handleEndGrowClick = () => {
    setShowEndGrowDialog(true);
  };

  const handleEndGrow = (action) => {
    if (activeGrowId) {
      endGrow(activeGrowId, action);
    }
  };

  const handleSaveName = () => {
    if (activeGrowId && editedName.trim()) {
      updateGrow(activeGrowId, { name: editedName.trim() });
    }
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'Escape') {
      setEditedName(activeGrow?.name || '');
    }
  };

  const handleTraitChange = (field, value) => {
    if (activeGrowId) {
      updateGrow(activeGrowId, {
        traits: { ...traits, [field]: value },
      });
    }
  };

  // Tags are stored as an array in traits.tags
  const tags = traits.tags || [];

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      const newTags = tagInput.split(',').map(t => t.trim()).filter(t => t && !tags.includes(t));
      if (newTags.length > 0) {
        handleTraitChange('tags', [...tags, ...newTags]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    handleTraitChange('tags', tags.filter(t => t !== tagToRemove));
  };

  // No active grow - show start button
  if (!activeGrow) {
    return (
      <>
        <Card
          variant="outlined"
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif' }}>
            {grows.length === 0 ? 'No active grow' : 'Select a grow or start a new one'}
          </Typography>
          {grows.filter(g => g.status === 'active').length > 0 && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Select Grow</InputLabel>
              <Select
                value=""
                onChange={(e) => setActiveGrowId(e.target.value)}
                label="Select Grow"
              >
                {grows.filter(g => g.status === 'active').map((grow) => (
                  <MenuItem key={grow.id} value={grow.id}>
                    {grow.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setShowStartDialog(true)}
          >
            New Grow
          </Button>
        </Card>
        <StartGrowDialog
          open={showStartDialog}
          onClose={() => setShowStartDialog(false)}
          onStart={handleStartGrow}
          presets={presets}
        />
      </>
    );
  }

  // Active grow - show controls
  return (
    <>
      <Card variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Top row: Grow name and controls */}
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            {/* Grow name */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {showDetails ? (
                <TextField
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onBlur={handleSaveName}
                  onKeyDown={handleNameKeyDown}
                  size="small"
                  placeholder="Grow name"
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      fontFamily: 'Inter, sans-serif',
                      py: 0.5,
                    },
                  }}
                />
              ) : (
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {activeGrow.name}
                </Typography>
              )}
            </Box>

            {/* Start date display */}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif' }}>
                Started: {new Date(activeGrow.startDate).toLocaleDateString()}
              </Typography>
            </Stack>

            {/* Preset selector - only in edit mode */}
            {showDetails && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={activeGrow.presetId || ''}
                  onChange={(e) => updateGrow(activeGrowId, { presetId: e.target.value })}
                  displayEmpty
                  sx={{
                    '& .MuiSelect-select': {
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontFamily: 'Inter, sans-serif',
                    },
                  }}
                  renderValue={(value) => {
                    const preset = presets.find(p => p.id === value);
                    return (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <SettingsIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" sx={{ fontFamily: 'Inter, sans-serif' }}>
                          {preset?.name || 'Select Preset'}
                        </Typography>
                      </Stack>
                    );
                  }}
                >
                  {presets.map((preset) => (
                    <MenuItem key={preset.id} value={preset.id}>
                      <Stack>
                        <Typography variant="body2" sx={{ fontFamily: 'Inter, sans-serif' }}>
                          {preset.name}
                        </Typography>
                        {preset.description && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif' }}>
                            {preset.description}
                          </Typography>
                        )}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Actions */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Edit button to show details */}
            <Button
              size="small"
              variant={showDetails ? 'contained' : 'outlined'}
              startIcon={<EditIcon sx={{ fontSize: 16 }} />}
              onClick={() => setShowDetails(!showDetails)}
              sx={{
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.75rem',
                px: 1.5,
                py: 0.5,
              }}
            >
              {showDetails ? 'Done' : 'Edit'}
            </Button>
            <Tooltip title="End this grow">
              <IconButton
                size="small"
                onClick={handleEndGrowClick}
                sx={{
                  bgcolor: 'error.main',
                  color: 'white',
                  width: 32,
                  height: 32,
                  '&:hover': { bgcolor: 'error.dark' },
                }}
              >
                <StopIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Trait chips (always visible) */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {traits.plantType && (
              <TraitChip
                icon={<LocalFloristIcon />}
                label="Plant"
                value={PLANT_TYPE_OPTIONS.find(o => o.value === traits.plantType)?.label || traits.plantType}
              />
            )}
            <TraitChip
              icon={<HomeIcon />}
              label="Environment"
              value={ENVIRONMENT_OPTIONS.find(o => o.value === traits.environment)?.label || traits.environment || 'Indoor'}
            />
            <TraitChip
              icon={<WaterDropIcon />}
              label="Medium"
              value={MEDIUM_OPTIONS.find(o => o.value === traits.medium)?.label || traits.medium || 'Hydroponic'}
            />
            <TraitChip
              icon={<LightModeIcon />}
              label="Lighting"
              value={traits.lightingType || 'LED'}
            />
            {traits.areaSize && (
              <TraitChip
                icon={<SquareFootIcon />}
                label="Area"
                value={traits.areaSize}
              />
            )}
            {/* Tags */}
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onDelete={showDetails ? () => handleRemoveTag(tag) : undefined}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.7rem',
                  height: 24,
                  bgcolor: 'action.selected',
                }}
              />
            ))}
          </Stack>

          {/* Description display (always visible when set) */}
          {traits.description && !showDetails && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.8rem',
                fontStyle: 'italic',
                px: 1,
                py: 0.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              {traits.description}
            </Typography>
          )}

          {/* Collapsible details section */}
          <Collapse in={showDetails}>
            <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Stack spacing={2.5}>
                {/* Trait editors - Grid layout */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 2 }}>
                  {/* Plant Type */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif', mb: 0.5, display: 'block' }}>
                      Plant Type
                    </Typography>
                    <Select
                      value={traits.plantType || ''}
                      onChange={(e) => handleTraitChange('plantType', e.target.value)}
                      size="small"
                      fullWidth
                      displayEmpty
                    >
                      <MenuItem value="" disabled><em>Select...</em></MenuItem>
                      {PLANT_TYPE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  {/* Environment */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif', mb: 0.5, display: 'block' }}>
                      Environment
                    </Typography>
                    <Select
                      value={traits.environment || 'indoor'}
                      onChange={(e) => handleTraitChange('environment', e.target.value)}
                      size="small"
                      fullWidth
                    >
                      {ENVIRONMENT_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {opt.icon}
                            <span>{opt.label}</span>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  {/* Grow Medium */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif', mb: 0.5, display: 'block' }}>
                      Grow Medium
                    </Typography>
                    <Select
                      value={traits.medium || 'hydroponic'}
                      onChange={(e) => handleTraitChange('medium', e.target.value)}
                      size="small"
                      fullWidth
                    >
                      {MEDIUM_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  {/* Lighting Type */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif', mb: 0.5, display: 'block' }}>
                      Lighting Type
                    </Typography>
                    <Select
                      value={traits.lightingType || 'LED'}
                      onChange={(e) => handleTraitChange('lightingType', e.target.value)}
                      size="small"
                      fullWidth
                    >
                      {LIGHTING_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  {/* Area Size */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif', mb: 0.5, display: 'block' }}>
                      Area Size
                    </Typography>
                    <TextField
                      placeholder="e.g., 4x4 ft"
                      size="small"
                      value={traits.areaSize || ''}
                      onChange={(e) => handleTraitChange('areaSize', e.target.value)}
                      fullWidth
                    />
                  </Box>
                </Box>

                {/* Description editor */}
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif', mb: 0.5, display: 'block' }}>
                    Description
                  </Typography>
                  <TextField
                    placeholder="Add notes about this grow..."
                    multiline
                    rows={2}
                    size="small"
                    value={traits.description || ''}
                    onChange={(e) => handleTraitChange('description', e.target.value)}
                    fullWidth
                    sx={{
                      '& .MuiInputBase-root': {
                        alignItems: 'center',
                        minHeight: 60,
                      },
                      '& .MuiInputBase-input': {
                        textAlign: 'left',
                      },
                      '& textarea': {
                        display: 'flex',
                        alignItems: 'center',
                      },
                    }}
                  />
                </Box>

                {/* Tags input */}
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif', mb: 0.5, display: 'block' }}>
                    Tags
                  </Typography>
                  <TextField
                    placeholder="Type tags separated by commas, press Enter to add..."
                    size="small"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleAddTag}
                    fullWidth
                    helperText="Press Enter to add tags"
                  />
                </Box>
              </Stack>
            </Box>
          </Collapse>

          {/* Stage selector - responsive for mobile */}
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <ToggleButtonGroup
              value={activeGrow.currentStage}
              exclusive
              onChange={handleStageChange}
              size="small"
              sx={{
                display: 'flex',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                gap: { xs: 0.5, sm: 0 },
                '& .MuiToggleButton-root': {
                  px: { xs: 1, sm: 1.5 },
                  py: 0.5,
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  fontFamily: 'Inter, sans-serif',
                  textTransform: 'none',
                  flex: { xs: '1 1 45%', sm: '0 0 auto' },
                  minWidth: { xs: 'auto', sm: 'auto' },
                  borderRadius: { xs: '4px !important', sm: 0 },
                  border: { xs: '1px solid', sm: undefined },
                  borderColor: { xs: 'divider', sm: undefined },
                  '&:first-of-type': {
                    borderRadius: { xs: '4px !important', sm: '4px 0 0 4px !important' },
                  },
                  '&:last-of-type': {
                    borderRadius: { xs: '4px !important', sm: '0 4px 4px 0 !important' },
                  },
                },
              }}
            >
              {STAGE_ORDER.map((stage) => (
                <ToggleButton
                  key={stage}
                  value={stage}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        bgcolor: activeGrow.currentStage === stage ? 'primary.main' : 'action.hover',
                        color: activeGrow.currentStage === stage ? 'white' : 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.65rem',
                      }}
                    >
                      {GROW_STAGES[stage].order}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        display: { xs: 'none', sm: 'inline' },
                      }}
                    >
                      {GROW_STAGES[stage].label}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        display: { xs: 'inline', sm: 'none' },
                        fontSize: '0.65rem',
                      }}
                    >
                      {stage === 'lateFlower' ? 'Late' : GROW_STAGES[stage].label.split('/')[0]}
                    </Typography>
                  </Stack>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Stack>
      </Card>

      {/* End Grow Dialog */}
      <EndGrowDialog
        open={showEndGrowDialog}
        onClose={() => setShowEndGrowDialog(false)}
        onEndGrow={handleEndGrow}
        growName={activeGrow?.name || ''}
      />
    </>
  );
}
